import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldPlus, Users, X, Loader2 } from "lucide-react";

/**
 * Per-user ACL grant modal (R147a). Admins/PMs can grant a specific
 * resident access to a folder even when the visibility rule would block
 * them — e.g. an ARC chair on an admins-only contracts folder.
 * Parent owns state; this component is pure UI.
 */
export function AclDialog({ folder, users, busy, input, setInput,
                             onAdd, onRemove, onClose }) {
  if (!folder) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[500px]"
                     data-testid="docs-acl-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldPlus className="h-4 w-4 text-sky-400" /> Per-user access
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {folder.name} · visibility{" "}
            <Badge variant="outline" className="text-[10px] ml-1">{folder.visibility || "all"}</Badge>
            <br />
            Grants below can see this folder even when the visibility rule would block them
            (e.g. give an ARC chair access to an Admin-only contracts folder).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
                   placeholder="Enter user email or ID"
                   className="bg-white/[0.04] border-white/10 flex-1"
                   data-testid="docs-acl-input" />
            <Button onClick={onAdd} disabled={busy || !input.trim()}
                    className="bg-sky-500 hover:bg-sky-600" data-testid="docs-acl-add">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Grant"}
            </Button>
          </div>
          <div className="border border-white/10 rounded-md min-h-[80px] max-h-[240px] overflow-y-auto">
            {users.length === 0 ? (
              <div className="p-4 text-center text-xs text-zinc-500" data-testid="docs-acl-empty">
                <Users className="h-4 w-4 mx-auto mb-1 opacity-50" />
                No individual grants yet. The visibility rule alone controls who sees this folder.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {users.map((u) => (
                  <li key={u.id} className="flex items-center gap-2 p-2 text-xs"
                      data-testid={`docs-acl-user-${u.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-zinc-100 truncate">{u.name || u.email}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{u.email} · {u.role}</div>
                    </div>
                    <button onClick={() => onRemove(u.id)} disabled={busy}
                            className="text-zinc-500 hover:text-red-300 p-1"
                            data-testid={`docs-acl-remove-${u.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
